/**
 * Pure helpers for subscription + dates. Match SubscriptionDateService behavior.
 */
import moment from 'moment';
import { calculateSubscriptionProgress } from '../calendar/helpers';
import { resolveSubscriptionDisplayImageUri } from './planImage';
import type { SubscriptionData, DayConfig, DateServiceConfig } from './types';
import { ensureAsciiDateString } from '../../../shared/utils/apiParams';
import { formatMomentForApiYmd } from '../../../shared/utils/dateUtils';

/** day_off to array 0-6 (moment.day(): Sun=0, Sat=6). */
export function normalizeDayOff(dayOff: number | number[] | undefined): number[] {
  if (dayOff == null) {
    return [];
  }
  if (Array.isArray(dayOff)) {
    return dayOff.map(Number).filter(d => d >= 0 && d <= 6);
  }
  return Number(dayOff) >= 0 && Number(dayOff) <= 6 ? [Number(dayOff)] : [];
}

function asAsciiYmdSet(dates: string[] | null | undefined): Set<string> {
  const out = new Set<string>();
  for (const d of dates ?? []) {
    const ascii = ensureAsciiDateString(String(d)) || String(d).trim();
    if (ascii) {
      out.add(ascii);
    }
  }
  return out;
}

/** API `skipped_dates` — treat like off days; not active plan days. */
export function getSubscriptionSkippedDateSet(sub: SubscriptionData): Set<string> {
  return asAsciiYmdSet(sub.skipped_dates);
}

/**
 * API `paused_dates` (canonical). Skip wins over pause when both list a date.
 * Falls back to legacy `pause_dates` only if `paused_dates` is missing.
 */
export function getSubscriptionPausedDateSet(sub: SubscriptionData): Set<string> {
  const skipped = getSubscriptionSkippedDateSet(sub);
  const raw = Array.isArray(sub.paused_dates) ? sub.paused_dates : (sub.pause_dates ?? []);
  const paused = asAsciiYmdSet(raw);
  for (const d of skipped) {
    paused.delete(d);
  }
  return paused;
}

/** True when this calendar day is a weekly off-day for the subscription (subscription day_off, else org week_day_off). */
export function isSubscriptionDeliveryDayOff(
  date: string,
  sub: SubscriptionData,
  config: DateServiceConfig | null | undefined,
): boolean {
  const m = moment(date, 'YYYY-MM-DD', true);
  if (!m.isValid()) {
    return true;
  }
  const moment_day = m.day();
  const subscription_day_off = normalizeDayOff(sub.detail?.day_off);
  if (subscription_day_off.length > 0) {
    return subscription_day_off.includes(moment_day);
  }
  if (config && config.week_day_off !== 0) {
    const custom_day = moment_day === 0 ? 7 : moment_day;
    return custom_day === config.week_day_off;
  }
  return false;
}

/**
 * Backend-paused meal dates still in plan range (not weekly off / skipped / frozen).
 * Source: API `paused_dates`. Used for “y paused” remaining copy.
 * Skip and freeze win over pause for counting.
 */
export function getSubscriptionPausedMealDates(
  sub: SubscriptionData,
  config?: DateServiceConfig | null,
): string[] {
  const pause_set = getSubscriptionPausedDateSet(sub);
  if (pause_set.size === 0) {
    return [];
  }
  const freeze_set = asAsciiYmdSet(sub.freeze_dates);
  const expanded_range = getInclusiveDateRangeStrings(sub.start_date, sub.end_date);
  const start = moment(sub.start_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
  const end = moment(sub.end_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
  const paused: string[] = [];
  for (const date_str of expanded_range) {
    if (!pause_set.has(date_str)) {
      continue;
    }
    if (freeze_set.has(date_str)) {
      continue;
    }
    const m = moment(date_str, 'YYYY-MM-DD', true);
    if (!m.isValid()) {
      continue;
    }
    if (!start.isValid() || !end.isValid() || !m.isBetween(start, end, 'day', '[]')) {
      continue;
    }
    if (isSubscriptionDeliveryDayOff(date_str, sub, config)) {
      continue;
    }
    paused.push(date_str);
  }
  return [...new Set(paused)].sort((a, b) => moment(a, 'YYYY-MM-DD', true).diff(moment(b, 'YYYY-MM-DD', true)));
}

/**
 * All meal-delivery dates (plan boxes) for one subscription: in [start_date, end_date],
 * excluding frozen, paused (`paused_dates`), skipped, weekly off, and double-box D+1 days.
 */
export function getSubscriptionMealDeliveryDates(
  sub: SubscriptionData,
  subscriptions: SubscriptionData[],
  available_dates: string[],
  config?: DateServiceConfig | null,
): string[] {
  const expanded_range = getInclusiveDateRangeStrings(sub.start_date, sub.end_date);
  const full_range = expanded_range.length > 0 ? expanded_range : available_dates;
  const freeze_set = asAsciiYmdSet(sub.freeze_dates);
  const pause_set = getSubscriptionPausedDateSet(sub);
  const skip_set = getSubscriptionSkippedDateSet(sub);
  const start = moment(sub.start_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
  const end = moment(sub.end_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
  const double_consumed = new Set(
    getDeliveryDatesConsumedByDoubleBox(
      full_range,
      date => (sub.double_box_dates ?? []).some(d => (ensureAsciiDateString(String(d)) || String(d).trim()) === date),
      sub.double_box_dates ?? [],
    ),
  );

  const delivery_dates: string[] = [];
  for (const date_str of full_range) {
    const m = moment(date_str, 'YYYY-MM-DD', true);
    if (!m.isValid()) {
      continue;
    }
    if (!start.isValid() || !end.isValid() || !m.isBetween(start, end, 'day', '[]')) {
      continue;
    }
    if (freeze_set.has(date_str)) {
      continue;
    }
    if (pause_set.has(date_str)) {
      continue;
    }
    if (skip_set.has(date_str)) {
      continue;
    }
    if (isSubscriptionDeliveryDayOff(date_str, sub, config)) {
      continue;
    }
    if (double_consumed.has(date_str)) {
      continue;
    }
    delivery_dates.push(m.format('YYYY-MM-DD'));
  }

  return [...new Set(delivery_dates)].sort((a, b) => moment(a, 'YYYY-MM-DD', true).diff(moment(b, 'YYYY-MM-DD', true)));
}

/** Plan hero image: merged subscription fields, then catalog plan match (SubscriptionList parity). */
export function resolveMergedSubscriptionImageUri(
  sub: SubscriptionData,
  catalog_categories?: unknown[] | null,
): string | null {
  return resolveSubscriptionDisplayImageUri(sub, catalog_categories);
}

/** Old filterDatesBySubscription: in range, exclude freeze/pause/skip/day_off and double-box D+1. */
export function filterDatesBySubscription(dates: string[], subscription_id: number, subscriptions: SubscriptionData[], _dayConfigMap?: Record<string, DayConfig>): string[] {
  const sub = subscriptions.find(s => s.id === subscription_id);
  if (!sub) {
    return [];
  }
  const start = moment(sub.start_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
  const end = moment(sub.end_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
  if (!start.isValid() || !end.isValid()) {
    return [];
  }
  const freezeSet = asAsciiYmdSet(sub.freeze_dates);
  const pauseSet = getSubscriptionPausedDateSet(sub);
  const skipSet = getSubscriptionSkippedDateSet(sub);
  const dayOffSet = new Set(normalizeDayOff(sub.detail?.day_off));
  const double_anchors = sub.double_box_dates ?? [];
  const double_consumed = new Set(
    getDeliveryDatesConsumedByDoubleBox(
      dates,
      date => double_anchors.some(d => (ensureAsciiDateString(String(d)) || String(d).trim()) === date),
      double_anchors,
    ),
  );

  return dates.filter(dateStr => {
    const ascii = ensureAsciiDateString(dateStr) || dateStr;
    const m = moment(ascii, 'YYYY-MM-DD', true);
    if (!m.isValid()) {
      return false;
    }
    const momentDayOfWeek = m.day();
    return (
      m.isBetween(start, end, 'day', '[]') &&
      !freezeSet.has(ascii) &&
      !pauseSet.has(ascii) &&
      !skipSet.has(ascii) &&
      !double_consumed.has(ascii) &&
      !dayOffSet.has(momentDayOfWeek)
    );
  });
}

export function getDayConfigForDate(date: string, dayConfigMap: Record<string, DayConfig>): DayConfig | null {
  return dayConfigMap[date] ?? null;
}

export function isDateFrozen(date: string, freezeDates: string[]): boolean {
  return freezeDates.includes(date);
}

export function isDatePaused(date: string, pauseDates: string[]): boolean {
  return pauseDates.includes(date);
}

export function isDateSkipped(date: string, skippedDates: string[]): boolean {
  return skippedDates.includes(date);
}

export function isDateAvailable(date: string, availableDates: string[]): boolean {
  return availableDates.includes(date);
}

/**
 * Remaining display: “x remaining, y paused” when any backend-paused days remain; else plain remaining count label.
 * `x + y` matches the pre-split remaining total (active delivery + paused from today).
 */
export function formatMealDaysRemainingLabel(
  days_left: number,
  paused_days_left: number,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  const remaining = Math.max(0, days_left);
  const paused = Math.max(0, paused_days_left);
  if (paused > 0) {
    return t('calendar.deliveryDaysRemainingPaused', { remaining, paused });
  }
  return t('calendar.deliveryDaysRemaining', { count: remaining });
}

/**
 * Collect normalized `YYYY-MM-DD` double-box anchor dates across subscriptions.
 */
export function collectDoubleBoxAnchorDates(
  subscriptions: Array<{ double_box_dates?: string[] | null }>,
  extra_anchors: Iterable<string> = [],
): string[] {
  const out = new Set<string>();
  for (const sub of subscriptions) {
    for (const d of sub.double_box_dates ?? []) {
      const ascii = ensureAsciiDateString(String(d)) || String(d).trim();
      if (ascii) {
        out.add(ascii);
      }
    }
  }
  for (const d of extra_anchors) {
    const ascii = ensureAsciiDateString(String(d)) || String(d).trim();
    if (ascii) {
      out.add(ascii);
    }
  }
  return [...out];
}

/**
 * Menu / press lock — which calendar dates are reserved because of double-box.
 *
 * When D is a double-box anchor, **immediate next calendar day D+1 is always locked**,
 * regardless of freeze / pause / skip / off / whether D+1 was in `availableDates`.
 *
 * Visual two-column merge lives in `buildCalendarGridCellsWithDoubleBoxSpans`
 * (see `.cursor/rules/calendar-double-box-span.mdc`).
 */
export function getDeliveryDatesConsumedByDoubleBox(
  availableDates: string[],
  isDoubleBoxAnchor: (date: string) => boolean,
  known_anchors: Iterable<string> = [],
): string[] {
  const sorted = [
    ...new Set(availableDates.map(d => ensureAsciiDateString(String(d)) || String(d).trim()).filter(Boolean)),
  ].sort();
  const consumed = new Set<string>();

  const consume_after_anchor = (anchor_raw: string) => {
    const anchor = ensureAsciiDateString(String(anchor_raw)) || String(anchor_raw).trim();
    if (!anchor || !isDoubleBoxAnchor(anchor)) {
      return;
    }
    const next_m = moment(anchor, 'YYYY-MM-DD', true).add(1, 'day');
    if (!next_m.isValid()) {
      return;
    }
    consumed.add(next_m.format('YYYY-MM-DD'));
  };

  for (const date of sorted) {
    if (isDoubleBoxAnchor(date)) {
      consume_after_anchor(date);
    }
  }

  for (const raw of known_anchors) {
    consume_after_anchor(String(raw));
  }

  return [...consumed];
}

/**
 * Gate for enabling a **new** double-box on day D:
 * there must be at least one meal-delivery date strictly after D (before/on plan end).
 * Not used when D is already in API `double_box_dates`.
 */
export function canEnableNewDoubleBoxForDate(
  date_ymd: string,
  sub: SubscriptionData | null | undefined,
  subscriptions: SubscriptionData[],
  available_dates: string[],
  config?: DateServiceConfig | null,
): boolean {
  if (!sub) {
    return false;
  }
  const d = ensureAsciiDateString(String(date_ymd)) || String(date_ymd).trim();
  if (!d) {
    return false;
  }
  const already =
    (sub.double_box_dates ?? []).some(x => (ensureAsciiDateString(String(x)) || String(x).trim()) === d);
  if (already) {
    return true;
  }
  const delivery = getSubscriptionMealDeliveryDates(sub, subscriptions, available_dates, config);
  return delivery.some(x => x > d);
}

/** API subscription date strings (English month names). Always parse under `en` locale. */
export const SUBSCRIPTION_API_DATE_FORMATS = [
  'YYYY-MM-DD',
  'DD MMMM, YYYY',
  'DD MMMM YYYY',
  'Do MMMM, YYYY',
  'MMM DD, YYYY',
] as const;

export type CheckoutActiveDateRange = { start: string; end: string };

export type CheckoutStartDateRules = {
  minStartYmd: string;
  maxStartYmd: string;
  weekDayOff: number;
  activeDateRanges: CheckoutActiveDateRange[];
  /** User-selected subscription off days (moment.day() 0=Sun … 6=Sat). Disables those weekdays in the start-date picker. */
  userDayOffs?: number[];
};

/** Parse subscription start/end from API under English locale (safe when app locale is Arabic). */
export function parseSubscriptionApiDate(value: string | undefined | null): moment.Moment | null {
  if (value == null || typeof value !== 'string' || !value.trim()) {
    return null;
  }
  const prev_locale = moment.locale();
  try {
    moment.locale('en');
    const parsed = moment(value.trim(), [...SUBSCRIPTION_API_DATE_FORMATS], true);
    return parsed.isValid() ? parsed.clone().startOf('day') : null;
  } finally {
    moment.locale(prev_locale);
  }
}

/** True when date falls inside any active subscription [start, end] (inclusive). */
export function isDateWithinCheckoutBlockedRanges(
  date_ymd: string,
  active_date_ranges: CheckoutActiveDateRange[],
): boolean {
  const day = moment(ensureAsciiDateString(date_ymd) || date_ymd, 'YYYY-MM-DD', true);
  if (!day.isValid()) {
    return true;
  }
  for (const range of active_date_ranges) {
    const start = moment(ensureAsciiDateString(range.start) || range.start, 'YYYY-MM-DD', true);
    const end = moment(ensureAsciiDateString(range.end) || range.end, 'YYYY-MM-DD', true);
    if (start.isValid() && end.isValid() && day.isBetween(start, end, 'day', '[]')) {
      return true;
    }
  }
  return false;
}

/**
 * Earliest allowed checkout start: max(today + package_start_after, day after latest active subscription end).
 * Uses normalized `activeDateRanges` from subscription fetch (YYYY-MM-DD).
 */
export function computeCheckoutMinStartYmd(
  package_start_after: number,
  active_date_ranges: CheckoutActiveDateRange[],
): string {
  const today = moment().locale('en').startOf('day');
  let min = today.clone().add(Math.max(0, package_start_after), 'days');

  if (active_date_ranges.length > 0) {
    let latest_end: moment.Moment | null = null;
    for (const range of active_date_ranges) {
      const end = moment(range.end, 'YYYY-MM-DD', true);
      if (end.isValid() && (!latest_end || end.isAfter(latest_end))) {
        latest_end = end.clone();
      }
    }
    if (latest_end) {
      const day_after_latest = latest_end.clone().add(1, 'day').startOf('day');
      if (day_after_latest.isAfter(min)) {
        min = day_after_latest;
      }
    }
  }

  return formatMomentForApiYmd(min);
}

export function isCheckoutWeekDayOff(date_ymd: string, week_day_off: number): boolean {
  if (week_day_off === 0) {
    return false;
  }
  return isDateDayOffWithConfig(date_ymd, {
    menu_selection_before: 0,
    package_start_after: 0,
    week_day_off,
  });
}

/** Whether a calendar day can be chosen as a new subscription start date. */
export function isCheckoutStartDateSelectable(date_ymd: string, rules: CheckoutStartDateRules): boolean {
  const ymd = ensureAsciiDateString(date_ymd) || date_ymd;
  const day = moment(ymd, 'YYYY-MM-DD', true);
  if (!day.isValid()) {
    return false;
  }
  const min = moment(ensureAsciiDateString(rules.minStartYmd) || rules.minStartYmd, 'YYYY-MM-DD', true);
  const max = moment(ensureAsciiDateString(rules.maxStartYmd) || rules.maxStartYmd, 'YYYY-MM-DD', true);
  if (!min.isValid() || !max.isValid()) {
    return false;
  }
  if (day.isBefore(min, 'day') || day.isAfter(max, 'day')) {
    return false;
  }
  if (isDateWithinCheckoutBlockedRanges(ymd, rules.activeDateRanges)) {
    return false;
  }
  if (isCheckoutWeekDayOff(ymd, rules.weekDayOff)) {
    return false;
  }
  const user_offs = rules.userDayOffs;
  if (user_offs != null && user_offs.length > 0 && user_offs.includes(day.day())) {
    return false;
  }
  return true;
}

/** Scan forward from `from_ymd` for the first selectable checkout start (skips org week off + active ranges). */
export function findFirstSelectableCheckoutStartYmd(
  from_ymd: string,
  rules: CheckoutStartDateRules,
  max_scan_days = 730,
): string | null {
  let current = moment(ensureAsciiDateString(from_ymd) || from_ymd, 'YYYY-MM-DD', true);
  if (!current.isValid()) {
    return null;
  }
  for (let i = 0; i < max_scan_days; i++) {
    const ymd = formatMomentForApiYmd(current);
    if (isCheckoutStartDateSelectable(ymd, rules)) {
      return ymd;
    }
    current = current.add(1, 'day');
  }
  return null;
}

/** Global day off: config.week_day_off with moment.day() 0→7 (Sun=7). */
export function isDateDayOffWithConfig(date: string, config: DateServiceConfig | null): boolean {
  if (!config || config.week_day_off === 0) {
    return false;
  }
  const momentDayOfWeek = moment(date, 'YYYY-MM-DD', true).day();
  const customDayOfWeek = momentDayOfWeek === 0 ? 7 : momentDayOfWeek;
  return customDayOfWeek === config.week_day_off;
}

/** True when the date falls within any active subscription's [start_date, end_date]. */
export function isDateInSubscriptionRange(date: string, subscriptions: SubscriptionData[]): boolean {
  const m = moment(date, 'YYYY-MM-DD', true);
  if (!m.isValid()) {return false;}
  for (const sub of subscriptions) {
    const start = moment(sub.start_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
    const end = moment(sub.end_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
    if (!start.isValid() || !end.isValid()) {continue;}
    if (m.isBetween(start, end, 'day', '[]')) {return true;}
  }
  return false;
}

/** Per-subscription day off: subscription detail.day_off (0-6), else fallback to config.
 * Also treats API `skipped_dates` like off days. Dates outside all active subscription ranges are day off (x). */
export function isDateDayOff(date: string, subscriptions: SubscriptionData[], config: DateServiceConfig | null): boolean {
  const m = moment(date, 'YYYY-MM-DD', true);
  if (!m.isValid()) {
    return false;
  }
  const date_ascii = ensureAsciiDateString(date) || date;
  const momentDayOfWeek = m.day();

  let dateWithinAnySubscription = false;
  for (const sub of subscriptions) {
    const start = moment(sub.start_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
    const end = moment(sub.end_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
    if (!start.isValid() || !end.isValid()) {
      continue;
    }
    const inRange = m.isBetween(start, end, 'day', '[]');
    if (!inRange) {
      continue;
    }
    dateWithinAnySubscription = true;
    if (getSubscriptionSkippedDateSet(sub).has(date_ascii)) {
      return true;
    }
    const subscriptionDayOff = normalizeDayOff(sub.detail?.day_off);
    if (subscriptionDayOff.length > 0) {
      return subscriptionDayOff.includes(momentDayOfWeek);
    }
  }
  if (!dateWithinAnySubscription) {
    return true;
  }
  return isDateDayOffWithConfig(date, config);
}

/** Old getNextAvailableDate(fromDate, skipActiveDays): advance by day, count active days (available, !freeze, !dayOff), return date after skip. */
export function getNextAvailableDateWithSkip(
  fromDate: string,
  skipActiveDays: number,
  availableDates: string[],
  freezeDates: string[],
  config: DateServiceConfig | null,
  subscriptions: SubscriptionData[],
): string | null {
  if (skipActiveDays <= 0) {
    const sorted = [...availableDates].sort();
    const idx = sorted.indexOf(fromDate);
    if (idx === -1 || idx >= sorted.length - 1) {
      return null;
    }
    return sorted[idx + 1] ?? null;
  }
  let current = moment(fromDate, 'YYYY-MM-DD', true).add(1, 'day');
  let activeCount = 0;
  const maxIterations = 365;
  let iterations = 0;

  while (activeCount < skipActiveDays && iterations < maxIterations) {
    iterations++;
    const dateStr = current.format('YYYY-MM-DD');
    const isDayOff = isDateDayOff(dateStr, subscriptions, config);
    const isFrozen = freezeDates.includes(dateStr);
    const isAvailable = availableDates.includes(dateStr);
    if (isAvailable && !isDayOff && !isFrozen) {
      activeCount++;
    }
    if (activeCount >= skipActiveDays) {
      return availableDates.includes(dateStr) ? dateStr : null;
    }
    current = current.add(1, 'day');
  }
  return null;
}

/** Next date in sorted availableDates list (skipActiveDays = 0). */
export function getNextAvailableDate(currentDate: string, availableDates: string[]): string | null {
  const sorted = [...availableDates].sort();
  const idx = sorted.indexOf(currentDate);
  if (idx === -1 || idx >= sorted.length - 1) {
    return null;
  }
  return sorted[idx + 1] ?? null;
}

export function getPreviousAvailableDate(currentDate: string, availableDates: string[]): string | null {
  const sorted = [...availableDates].sort();
  const idx = sorted.indexOf(currentDate);
  if (idx <= 0) {
    return null;
  }
  return sorted[idx - 1] ?? null;
}

/** Dates from particularsDaily where address_id matches (old getAddressDates). */
export function getAddressDates(addressId: number, subscriptions: SubscriptionData[]): string[] {
  const dates: string[] = [];
  const target_id = Number(addressId);
  if (!Number.isFinite(target_id) || target_id <= 0) {
    return dates;
  }
  const subs = Array.isArray(subscriptions) ? subscriptions : [];
  for (const sub of subs) {
    const particulars = sub.particularsDaily ?? [];
    for (const p of particulars) {
      const addr_id = Number((p as { address_id?: number | string }).address_id);
      if (addr_id === target_id && p.date && moment(p.date, 'YYYY-MM-DD', true).isValid()) {
        dates.push(moment(p.date).format('YYYY-MM-DD'));
      }
    }
  }
  return [...new Set(dates)].sort((a, b) => moment(a).diff(moment(b)));
}

/** Old isDateAvailableForCheckout: in [minDateForCheckout, maxDateForCheckout], not in any activeDateRange, not day off. */
export function isDateAvailableForCheckout(
  date: string,
  minDateForCheckout: string,
  maxDateForCheckout: string,
  activeDateRanges: Array<{ start: string; end: string }>,
  isDateDayOffFn: (date: string) => boolean,
): boolean {
  const m = moment(date, 'YYYY-MM-DD', true);
  if (!m.isValid()) {
    return false;
  }
  if (m.isBefore(moment(minDateForCheckout)) || m.isAfter(moment(maxDateForCheckout))) {
    return false;
  }
  for (const range of activeDateRanges) {
    if (m.isBetween(moment(range.start), moment(range.end), 'day', '[]')) {
      return false;
    }
  }
  if (isDateDayOffFn(date)) {
    return false;
  }
  return true;
}

/**
 * Contract plan duration in days from subscription **`detail.day`** or **`detail.days`** only.
 * Does not use the span between start/end dates (that is not plan duration).
 * Also checks `subscription_detail` when present.
 */
/** Every calendar day from subscription start through end (inclusive), YYYY-MM-DD. Used for meal progress; not menu-selectable dates. */
export function getInclusiveDateRangeStrings(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  const current = moment(startDate, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
  const end = moment(endDate, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
  if (!current.isValid() || !end.isValid()) {
    return dates;
  }
  while (current.isSameOrBefore(end, 'day')) {
    dates.push(current.format('YYYY-MM-DD'));
    current.add(1, 'day');
  }
  return dates;
}

export function getPlanDurationDaysFromSubscription(sub: SubscriptionData): number {
  const d = sub.detail;
  let n = Number(d?.day) || Number(d?.days) || 0;
  if (n > 0) {
    return Math.floor(n);
  }
  const sd = sub.subscription_detail;
  if (sd != null && typeof sd === 'object') {
    const o = sd as Record<string, unknown>;
    n = Number(o.day) || Number(o.days) || 0;
  }
  return n > 0 ? Math.floor(n) : 0;
}

/**
 * Plan length for calendar-style progress (`detail.day` / `days`, else inclusive calendar span, else 30).
 *
 * Used by: AccountsScreen, DashboardStickyHeader, MySubscriptionsScreen, legacy calendar math.
 */
export function getTotalPlanDaysFromMergedSubscription(sub: SubscriptionData): number {
  const from_contract = getPlanDurationDaysFromSubscription(sub);
  if (from_contract > 0) {
    return from_contract;
  }
  const start = moment(sub.start_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
  const end = moment(sub.end_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
  if (start.isValid() && end.isValid()) {
    return Math.max(1, end.diff(start, 'days') + 1);
  }
  return 30;
}

/**
 * Calendar-aligned progress from merged subscription (same math as Calendar tab / `calculateSubscriptionProgress`).
 *
 * Used by: AccountsScreen, CalendarScreen, DashboardStickyHeader.
 */
export function getCalendarProgressFromMergedSubscription(sub: SubscriptionData) {
  const totalPlanDaysDisplay = getTotalPlanDaysFromMergedSubscription(sub);
  const { daysLeft, daysElapsed, progressPercentage } = calculateSubscriptionProgress(
    sub.start_date,
    sub.end_date,
    totalPlanDaysDisplay,
  );
  return { daysLeft, daysElapsed, progressPercentage, totalPlanDaysDisplay };
}

/**
 * Meal-delivery progress:
 * - **Remaining** = active delivery dates **from today onward** (excludes freeze/pause/skip/off/double-box D+1).
 * - Uses the **full [start_date, end_date] calendar range** for delivery days — not `availableDates` from the API,
 *   which omits past dates (menu selection only) and would under-count remaining days.
 * - **Total / elapsed** use **`detail.day` / `detail.days`** only (contract duration), not start–end date count.
 * - Elapsed = plan_duration − remaining_delivery_days. Progress = elapsed / plan_duration.
 * - If contract duration is missing, falls back to counts from the filtered meal-date list only (no calendar span).
 */
export function getActiveMealDayProgressFromSubscription(
  sub: SubscriptionData,
  available_dates: string[],
  subscriptions: SubscriptionData[],
  day_config_map?: Record<string, DayConfig>,
  config?: DateServiceConfig | null,
): {
  daysLeft: number;
  /** Backend-paused delivery days from today onward (`x remaining + y paused` = prior remaining total). */
  pausedDaysLeft: number;
  daysElapsed: number;
  totalDays: number;
  progressPercentage: number;
} {
  const today_str = moment().format('YYYY-MM-DD');
  const paused_days_left = getSubscriptionPausedMealDates(sub, config).filter(d => d >= today_str).length;

  if (config !== undefined) {
    const sorted = getSubscriptionMealDeliveryDates(sub, subscriptions, available_dates, config);
    const days_left_delivery = sorted.filter(d => d >= today_str).length;
    const days_elapsed_delivery = sorted.filter(d => d < today_str).length;
    const total_delivery = sorted.length;
    const progress_percentage =
      total_delivery > 0 ? Math.min(100, Math.round((days_elapsed_delivery / total_delivery) * 100)) : 0;

    return {
      daysLeft: days_left_delivery,
      pausedDaysLeft: paused_days_left,
      daysElapsed: days_elapsed_delivery,
      totalDays: Math.max(total_delivery, 1),
      progressPercentage: progress_percentage,
    };
  }

  const expanded_range = getInclusiveDateRangeStrings(sub.start_date, sub.end_date);
  const full_range = expanded_range.length > 0 ? expanded_range : available_dates;
  const meal_dates = filterDatesBySubscription(full_range, sub.id, subscriptions, day_config_map);
  const sorted = [...new Set(meal_dates.map(d => String(d).trim()))].sort((a, b) =>
    moment(a, 'YYYY-MM-DD', true).diff(moment(b, 'YYYY-MM-DD', true)),
  );
  const days_left_delivery = sorted.filter(d => d >= today_str).length;
  const plan_duration = getPlanDurationDaysFromSubscription(sub);

  if (plan_duration > 0) {
    const days_elapsed = Math.max(0, plan_duration - days_left_delivery);
    const progress_percentage = Math.min(100, Math.round((days_elapsed / plan_duration) * 100));
    return {
      daysLeft: days_left_delivery,
      pausedDaysLeft: paused_days_left,
      daysElapsed: Math.min(plan_duration, days_elapsed),
      totalDays: plan_duration,
      progressPercentage: progress_percentage,
    };
  }

  const total_meal = sorted.length;
  const elapsed_meal = sorted.filter(d => d < today_str).length;
  const progress_percentage =
    total_meal > 0 ? Math.min(100, Math.round((elapsed_meal / total_meal) * 100)) : 0;
  return {
    daysLeft: days_left_delivery,
    pausedDaysLeft: paused_days_left,
    daysElapsed: elapsed_meal,
    totalDays: Math.max(total_meal, 1),
    progressPercentage: progress_percentage,
  };
}

/**
 * First freeze/unfreeze-capable calendar day — same floor as menu selection
 * (`today + menu_selection_before`, inclusive).
 */
export function getMinFreezeToggleDate(menu_selection_before: number, from_date?: moment.MomentInput): moment.Moment {
  const offset = Number.isFinite(menu_selection_before) ? Math.max(0, Math.floor(menu_selection_before)) : 3;
  return moment(from_date).startOf('day').add(offset, 'days');
}

export function getMinFreezeToggleDateYmd(menu_selection_before: number, from_date?: moment.MomentInput): string {
  return formatMomentForApiYmd(getMinFreezeToggleDate(menu_selection_before, from_date));
}

/** True when the customer may freeze/unfreeze this date (on or after menu unlock day). */
export function isDateFreezeToggleable(date: string, menu_selection_before: number, from_date?: moment.MomentInput): boolean {
  const m = moment(date, 'YYYY-MM-DD', true);
  if (!m.isValid()) {
    return false;
  }
  return !m.isBefore(getMinFreezeToggleDate(menu_selection_before, from_date), 'day');
}

export function filterFreezeToggleableDates(
  dates: string[],
  menu_selection_before: number,
  from_date?: moment.MomentInput,
): string[] {
  return dates.filter(d => isDateFreezeToggleable(d, menu_selection_before, from_date));
}
